import { motion } from "framer-motion";
import { Star, BadgeCheck, Quote } from "lucide-react";
import { Reveal } from "./Reveal";

const featured = {
  quote:
    "For two years that garage held everything except our car. milla cleared it in an afternoon — and somehow cleared my head, too.",
  before: "Overwhelmed every time I opened the door",
  after: "Calm. We park inside now.",
  name: "Dana R.",
  place: "Newburyport, MA",
};

const reviews = [
  {
    quote: "I finally parked in my garage.",
    name: "Marcus T.",
    place: "Portsmouth, NH",
    tone: "bg-sand/50",
  },
  {
    quote: "Our basement became the office I always needed.",
    name: "Priya N.",
    place: "Lexington, MA",
    tone: "bg-sage/40",
  },
  {
    quote: "I didn't realize how much the clutter was weighing on me — until it was gone.",
    name: "Elena M.",
    place: "Exeter, NH",
    tone: "bg-lavender/60",
  },
];

const marquee = [
  "I can breathe in here again",
  "Lighter the second they left",
  "It felt like a fresh start",
  "My weekends are mine again",
  "Kind, fast, gone before lunch",
  "Wish I'd called sooner",
  "It felt like magic",
];

const Stars = ({ className = "" }) => (
  <div className={`flex gap-0.5 ${className}`} aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={15} className="fill-violet text-violet" />
    ))}
  </div>
);

export default function Reviews() {
  return (
    <section id="reviews" className="overflow-hidden py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* header */}
        <div className="max-w-2xl">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet/70">In their words</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl leading-[1.02] tracking-tight text-charcoal sm:text-5xl lg:text-6xl text-balance">
              The room changed. So did the feeling.
            </h2>
          </Reveal>
        </div>

        {/* featured + supporting */}
        <div className="mt-14 grid gap-6 lg:grid-cols-12">
          {/* featured */}
          <Reveal delay={0.05} className="lg:col-span-7">
            <figure className="relative flex h-full flex-col justify-between overflow-hidden rounded-[2.2rem] bg-violet p-9 text-white sm:p-12">
              <Quote className="absolute right-8 top-8 text-white/15" size={72} />
              <div>
                <Stars className="[&_svg]:fill-lavender [&_svg]:text-lavender" />
                <blockquote className="mt-6 font-display text-3xl leading-[1.12] tracking-tight sm:text-[2.5rem] text-balance">
                  "{featured.quote}"
                </blockquote>
              </div>

              <div className="mt-10">
                <div className="flex flex-wrap gap-3">
                  <span className="rounded-full bg-white/10 px-4 py-2 text-sm text-lavender/90">
                    Before · {featured.before}
                  </span>
                  <span className="rounded-full bg-white/15 px-4 py-2 text-sm font-medium text-white">
                    After · {featured.after}
                  </span>
                </div>
                <figcaption className="mt-7 flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-lavender font-display text-lg text-violet">
                    {featured.name.charAt(0)}
                  </span>
                  <span>
                    <span className="flex items-center gap-1.5 font-semibold">
                      {featured.name}
                      <BadgeCheck size={16} className="text-lavender" />
                    </span>
                    <span className="block text-sm text-white/60">{featured.place} · Verified pickup</span>
                  </span>
                </figcaption>
              </div>
            </figure>
          </Reveal>

          {/* supporting */}
          <div className="grid gap-6 lg:col-span-5">
            {reviews.map((r, i) => (
              <Reveal key={r.name} delay={0.1 + i * 0.08}>
                <figure
                  data-testid={`review-${i}`}
                  className={`flex flex-col justify-between rounded-[2.2rem] ${r.tone} p-7 sm:p-8`}
                >
                  <div>
                    <Stars />
                    <blockquote className="mt-4 font-display text-xl leading-snug tracking-tight text-charcoal sm:text-2xl text-balance">
                      "{r.quote}"
                    </blockquote>
                  </div>
                  <figcaption className="mt-6 flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/70 font-display text-violet">
                      {r.name.charAt(0)}
                    </span>
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-charcoal">
                      {r.name}
                      <span className="font-normal text-charcoal/50">· {r.place}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* emotional marquee */}
      <div className="relative mt-14 flex select-none overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
        <motion.div
          className="flex shrink-0 items-center gap-4 pr-4"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 32, ease: "linear", repeat: Infinity }}
        >
          {[...marquee, ...marquee].map((phrase, i) => (
            <span
              key={i}
              className="flex items-center gap-3 whitespace-nowrap rounded-full bg-white/60 px-6 py-3 font-display text-xl italic text-charcoal/80"
            >
              {phrase}
              <span className="h-1.5 w-1.5 rounded-full bg-violet/40" />
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
